package com.isaias.fyattravel.web.components

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.layout.ResponsiveStyles
import org.jetbrains.compose.web.attributes.InputType
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.web.theme.ThemeColors

private const val CAR_WHATSAPP_NUMBER = "256758250625"
private val CAR_EMAIL_REGEX = Regex("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$")

/**
 * Car rental inquiry card component. Collects rental details and sends them
 * to info@fyattraveltour.com or WhatsApp instead of performing a live search.
 */
@Composable
fun CarRentalSearchCard() {
    var pickupQuery by remember { mutableStateOf("New York") }
    var dropoffQuery by remember { mutableStateOf("") }
    var pickUpDate by remember { mutableStateOf("2026-01-02") }
    var dropOffDate by remember { mutableStateOf("2026-01-10") }
    var pickUpTime by remember { mutableStateOf("10:00") }
    var dropOffTime by remember { mutableStateOf("10:00") }
    var driverAge by remember { mutableStateOf("30") }
    var email by remember { mutableStateOf("") }

    var sending by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var attemptedSend by remember { mutableStateOf(false) }
    val locale = WebLocaleStore.locale.value
    fun t(key: WebStringKey) = WebStrings.get(locale, key)

    fun inquiryFields() = linkedMapOf(
        t(WebStringKey.CAR_PICKUP_CITY) to pickupQuery.trim(),
        t(WebStringKey.CAR_DROPOFF_CITY_OPTIONAL) to dropoffQuery.trim(),
        t(WebStringKey.CAR_PICKUP_DATE) to pickUpDate.trim(),
        t(WebStringKey.CAR_DROPOFF_DATE) to dropOffDate.trim(),
        t(WebStringKey.CAR_PICKUP_TIME) to pickUpTime.trim(),
        t(WebStringKey.CAR_DROPOFF_TIME) to dropOffTime.trim(),
        t(WebStringKey.CAR_DRIVER_AGE) to driverAge.trim(),
    )

    Div({
        style {
            backgroundColor(ThemeColors.surface)
            padding(1.75.em)
            borderRadius(1.em)
            property("box-shadow", "0 6px 18px ${ThemeColors.shadow}")
            marginBottom(2.em)
        }
    }) {
        Div({
            classes(ResponsiveStyles.carSearchGrid)
            style {
                display(DisplayStyle.Grid)
                gap(1.em)
                alignItems(AlignItems.End)
            }
        }) {
            TextInputBox(t(WebStringKey.CAR_PICKUP_CITY), pickupQuery) { pickupQuery = it }
            TextInputBox(t(WebStringKey.CAR_DROPOFF_CITY_OPTIONAL), dropoffQuery) { dropoffQuery = it }
            TextInputBox(t(WebStringKey.CAR_PICKUP_DATE), pickUpDate) { pickUpDate = it }
            TextInputBox(t(WebStringKey.CAR_DROPOFF_DATE), dropOffDate) { dropOffDate = it }
            TextInputBox(t(WebStringKey.CAR_PICKUP_TIME), pickUpTime) { pickUpTime = it }
            TextInputBox(t(WebStringKey.CAR_DROPOFF_TIME), dropOffTime) { dropOffTime = it }
            TextInputBox(t(WebStringKey.CAR_DRIVER_AGE), driverAge) { driverAge = it }
            EmailInputBox(t(WebStringKey.EMAIL_LABEL), email, attemptedSend && !CAR_EMAIL_REGEX.matches(email.trim())) { email = it }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(0.75.em)
                marginTop(1.em)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Button({
                style {
                    backgroundColor(if (sending) ThemeColors.primaryMuted else ThemeColors.primary)
                    color(ThemeColors.textPrimary)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor(if (sending) "not-allowed" else "pointer")
                    fontWeight("700")
                    property("box-shadow", "0 4px 12px ${ThemeColors.shadow}")
                }
                onClick {
                    attemptedSend = true
                    successMessage = null
                    if (sending) return@onClick
                    if (!CAR_EMAIL_REGEX.matches(email.trim())) {
                        error = t(WebStringKey.VALIDATION_ENTER_VALID_EMAIL)
                        return@onClick
                    }
                    sending = true
                    error = null

                    submitInquiry(type = "car", email = email.trim(), fields = inquiryFields()) { ok, message ->
                        sending = false
                        if (ok) {
                            successMessage = t(WebStringKey.ENQUIRY_SENT)
                            attemptedSend = false
                        } else {
                            error = message ?: t(WebStringKey.ERROR_NETWORK)
                        }
                    }
                }
            }) { Text(if (sending) t(WebStringKey.PLEASE_WAIT) else t(WebStringKey.SEND_EMAIL)) }

            Button({
                style {
                    backgroundColor(ThemeColors.whatsapp)
                    color(ThemeColors.textPrimary)
                    padding(0.9.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                }
                onClick {
                    val message = buildString {
                        appendLine(t(WebStringKey.CAR_RENTAL))
                        inquiryFields().forEach { (label, value) -> appendLine("$label: $value") }
                        if (email.isNotBlank()) appendLine("${t(WebStringKey.EMAIL_LABEL)}: ${email.trim()}")
                    }
                    val encoded = js("encodeURIComponent")(message) as String
                    window.open("https://wa.me/$CAR_WHATSAPP_NUMBER?text=$encoded", "_blank")
                }
            }) { Text(t(WebStringKey.WHATSAPP)) }
        }

        error?.let { msg ->
            Br()
            P({
                style {
                    color(ThemeColors.error)
                    fontWeight("600")
                }
            }) { Text(msg) }
        }

        successMessage?.let { msg ->
            Br()
            P({
                style {
                    color(ThemeColors.success)
                    fontWeight("600")
                }
            }) { Text(msg) }
        }
    }
}

@Composable
private fun TextInputBox(label: String, value: String, onValueChange: (String) -> Unit) {
    Div {
        Label(null, {
            style {
                fontSize(0.8.em)
                color(ThemeColors.textSecondary)
                marginBottom(0.25.em)
                display(DisplayStyle.Block)
            }
        }) { Text(label) }
        Input(type = InputType.Text, attrs = {
            this.value(value)
            onInput { onValueChange(it.value) }
            style {
                width(100.percent)
                padding(0.75.em)
                borderRadius(0.5.em)
                property("border", "1px solid ${ThemeColors.borderLight}")
                backgroundColor(ThemeColors.surface)
                fontWeight("600")
            }
        })
    }
}
